<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <form @submit.prevent="validateForm">
        <div class="row">
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Código</label>
              <input type="text" class="form-control h-45" placeholder="Ingresar el código del trofeo"
                v-model.trim="prize.code" @input="clearError('code')" />
              <div class="input-error">{{ errors.code }}</div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Nombre</label>
              <input type="text" class="form-control h-45" placeholder="Ingresar el nombre del trofeo"
                v-model.trim="prize.name" @input="clearError('name')" />
              <div class="input-error">{{ errors.name }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-8 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Descripción</label>
              <textarea rows="4" style="resize: none" class="form-control text-dark no-resize"
                placeholder="Ingresar la descripción del trofeo" v-model.trim="prize.description"
                @input="clearError('description')"></textarea>
              <div v-if="errors.description" class="input-error">
                {{ errors.description }}
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <div class="form-group mb-4">
              <label class="label text-secondary">Foto del Trofeo</label>

              <div v-if="prize.urlPhoto">
                <img :src="prize.urlPhoto" alt="Foto del Trofeo" class="img-fluid mb-2 d-block" /> <button
                  @click.prevent="removePhoto" class="btn btn-danger btn-sm">
                  Eliminar foto
                </button>
              </div>

              <FilePond name="FilePhoto" :maxFileSize="'2MB'" :accepted-file-types="supportedFormats"
                allow-multiple="false" :max-files="1"
                label-idle="Arrastra tu imagen o <span class='filepond--label-action'>Buscar</span>"
                :labelMaxFileSizeExceeded="'El archivo es demasiado grande'"
                :labelMaxFileSize="'El tamaño máximo permitido es {filesize}'"
                :labelFileTypeNotAllowed="'El archivo tiene una extensión no permitida'"
                :fileValidateTypeLabelExpectedTypes="`Los tipos de archivo permitidos son: ${supportedFormats.join(', ')}`"
                credits="" :allow-image-preview="true" :image-preview-height="200" @updatefiles="handleUpdateFiles"
                @input="clearError('filePhoto')" v-if="!prize.urlPhoto" />
              <div class="input-error">{{ errors.filePhoto }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <div class="d-flex flex-wrap gap-3">
              <RouterLink to="/prizes">
                <button type="button"
                  class="btn btn-danger bg-danger bg-opacity-10 text-danger border-0 fw-semibold py-2 px-4">
                  Cancelar
                </button>
              </RouterLink>
              <button class="btn btn-primary bg-primary bg-opacity-10 text-primary border-0 fw-semibold py-2 px-4"
                type="submit" :disabled="isLoading">
                <span v-if="!isLoading">{{
                  prize.id == 0 ? "Crear" : "Actualizar"
                }}</span>
                <span class="spinner-border spinner-border-sm me-2" aria-hidden="true" v-if="isLoading"></span>
                <span role="status" v-if="isLoading">Procesando...</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { prizeSchema } from "@/schemas/prizeSchema";
import { defineComponent, ref, onMounted } from "vue";
import api from "@/services/api";
import { useToast } from "vue-toastification";
import { useRouter, useRoute } from "vue-router";
import vueFilePond from "vue-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";

// Create FilePond component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview
);

export default defineComponent({
  name: "CreatePrize",
  components: {
    FilePond: FilePond as any,
  },
  setup() {
    const toast = useToast();
    const route = useRoute();
    const router = useRouter();
    const isLoading = ref(false);

    const supportedFormats = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp"
    ];

    const prize = ref({
      id: 0,
      code: "",
      name: "",
      description: "",
      status: true,
      photo: "" as string | null,
      filePhoto: null as File | null,
      urlPhoto: null as string | null,
      prizeCategoryId: null,
    });
    const errors = ref<Record<string, string>>({});

    onMounted(async () => {
      const id = route.params.id;
      if (id) {
        await getPrizeById(id);
      }
    });

    const validateForm = async () => {
      try {
        errors.value = {};
        await prizeSchema.validate(prize.value, {
          abortEarly: false,
        });
        isLoading.value = true;
        await createPrize();
        isLoading.value = false;
      } catch (err: any) {
        console.log("err: " + err);
        if (err.inner) {
          err.inner.forEach((e: any) => {
            errors.value[e.path] = e.message;
          });
        }
        isLoading.value = false;
      }
    };

    const clearError = (field: string) => {
      errors.value[field] = "";
    };

    const getPrizeById = async (id: any) => {
      try {
        const response = await api.get(`/prize/maintenance/${id}`);
        prize.value = response.data;
        if (response.data.photo) {
          prize.value.urlPhoto = process.env.VUE_APP_API_URL + '/' + response.data.photo;
        }

      } catch (ex) {
        console.error('Error loading prize:', ex);
      }
    };

    const handleUpdateFiles = (fileItems: any[]) => {
      if (fileItems.length > 0) {
        prize.value.filePhoto = fileItems[0].file;
      } else {
        prize.value.filePhoto = null;
      }
    };

    const removePhoto = () => {
      if (prize.value.urlPhoto && prize.value.urlPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(prize.value.urlPhoto);
      }
      prize.value.urlPhoto = null;
      prize.value.photo = null;
      prize.value.filePhoto = null;
    };

    const buildFormData = (data: any) => {
      const formData = new FormData();
      for (const key in data) {
        if (
          data[key] !== undefined &&
          data[key] !== null &&
          key !== "createdBy" &&
          key !== "photo"
        ) {
          if (key === "filePhoto" && data[key]) {
            formData.append("FilePhoto", data[key]);
          } else {
            formData.append(key, data[key]);
          }
        }
      }
      return formData;
    };

    const createPrize = async () => {
      try {
        console.log('Creating prize:', prize.value);

        if (prize.value.filePhoto) {
          const formData = buildFormData(prize.value);
          const response: any = await api.postForm(`/prize`, formData);

          if (response.data != 0) {
            toast.success(
              `El Trofeo fue ${prize.value.id == 0 ? "creado" : "actualizado"
              } con éxito.`,
              {
                timeout: 2000,
              }
            );
            router.push({ name: "PrizesPage" });
          }
        } else {
          const payload = {
            id: prize.value.id,
            code: prize.value.code,
            name: prize.value.name,
            description: prize.value.description,
            status: prize.value.status,
            photo: prize.value.photo,
            prizeCategoryId: prize.value.prizeCategoryId,
          };

          const response: any = await api.post(`/prize`, payload);
          if (response.data != 0) {
            toast.success(
              `El Trofeo fue ${prize.value.id == 0 ? "creado" : "actualizado"
              } con éxito.`,
              {
                timeout: 2000,
              }
            );
            router.push({ name: "PrizesPage" });
          }
        }
      } catch (ex) {
        toast.error("El Trofeo no pudo ser creado.", {
          timeout: 2000,
        });
        console.error(ex);
      }
    };

    return {
      createPrize,
      validateForm,
      clearError,
      prize,
      errors,
      isLoading,
      supportedFormats,
      handleUpdateFiles,
      removePhoto,
    };
  },
});
</script>